/**
 * TierMarginsSection — editable grid for the global DSCR tier margins
 * (dscr_tier_margins collection, one row per tier). Margins / prices are in BOND
 * POINTS (par = 100), e.g. 0.375 = Gold margin. Loan-amount bounds are dollars.
 *
 * This is a small fixed-row config editor (a handful of tiers, edit-all-then-save)
 * rather than a sortable data grid, so it follows the existing PricingScreen
 * ArrangementsSection pattern (a hand-rolled editable table) instead of AG Grid —
 * inline numeric inputs across a fixed column set with a single Save per dirty row.
 */
import { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import LayersOutlined from "@mui/icons-material/LayersOutlined";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { useSnackbar } from "notistack";
import { useCorrespondentsApi } from "../../hooks/useCorrespondentsHook";
import { DSCR_MARGIN_TIERS } from "../../api/losLocksApi";
import PricingConfigVersionDialog from "./PricingConfigVersionDialog";

const sectionCardSx = {
  border: "1px solid #e5e7eb",
  borderRadius: 3.5,
  bgcolor: "#fff",
  overflow: "hidden",
};
const sectionTitleSx = { fontSize: 14, fontWeight: 600, color: "#0f172a" };

// Editable numeric columns (besides the tier name + label). points vs dollars.
const NUM_COLS = [
  { key: "marginPoints", label: "Margin (pts)", required: true, step: 0.001 },
  { key: "maxPrice", label: "Max Price", step: 0.001 },
  { key: "minPrice", label: "Min Price", step: 0.001 },
  { key: "minLoanAmount", label: "Min Loan ($)", step: 1000 },
  { key: "maxLoanAmount", label: "Max Loan ($)", step: 1000 },
];

const numOrNull = (v) =>
  v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v);
const cellStr = (v) => (v == null ? "" : String(v));

export default function TierMarginsSection() {
  const api = useCorrespondentsApi();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState(null); // map tier -> draft
  const [savedRows, setSavedRows] = useState({}); // map tier -> last-saved values
  const [loading, setLoading] = useState(true);
  const [savingTier, setSavingTier] = useState(null);
  const [historyTier, setHistoryTier] = useState(null);

  const load = () => {
    setLoading(true);
    return api
      .listTierMargins()
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const byTier = {};
        list.forEach((d) => {
          const tier = d._id ?? d.tier;
          if (tier) byTier[tier] = d;
        });
        // Always surface the known tiers even when not yet seeded.
        DSCR_MARGIN_TIERS.forEach((t) => {
          if (!byTier[t]) byTier[t] = { _id: t };
        });
        const drafts = {};
        Object.entries(byTier).forEach(([tier, d]) => {
          drafts[tier] = {
            label: cellStr(d.label),
            marginPoints: cellStr(d.marginPoints),
            maxPrice: cellStr(d.maxPrice),
            minPrice: cellStr(d.minPrice),
            minLoanAmount: cellStr(d.minLoanAmount),
            maxLoanAmount: cellStr(d.maxLoanAmount),
            notes: cellStr(d.notes),
          };
        });
        setRows(drafts);
        setSavedRows(JSON.parse(JSON.stringify(drafts)));
      })
      .catch(() => {
        // Unseeded / endpoint unavailable — still show the known tiers to edit.
        const drafts = {};
        DSCR_MARGIN_TIERS.forEach((t) => {
          drafts[t] = {
            label: "",
            marginPoints: "",
            maxPrice: "",
            minPrice: "",
            minLoanAmount: "",
            maxLoanAmount: "",
            notes: "",
          };
        });
        setRows(drafts);
        setSavedRows(JSON.parse(JSON.stringify(drafts)));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tiers = useMemo(() => (rows ? Object.keys(rows) : []), [rows]);

  const setCell = (tier, key, value) =>
    setRows((prev) => ({ ...prev, [tier]: { ...prev[tier], [key]: value } }));

  const isDirty = (tier) =>
    JSON.stringify(rows?.[tier]) !== JSON.stringify(savedRows?.[tier]);

  const saveTier = (tier) => {
    const draft = rows[tier];
    if (numOrNull(draft.marginPoints) == null) {
      enqueueSnackbar(`${tier}: Margin (pts) is required.`, { variant: "error" });
      return;
    }
    const body = {
      label: draft.label || undefined,
      marginPoints: numOrNull(draft.marginPoints),
      maxPrice: numOrNull(draft.maxPrice),
      minPrice: numOrNull(draft.minPrice),
      minLoanAmount: numOrNull(draft.minLoanAmount),
      maxLoanAmount: numOrNull(draft.maxLoanAmount),
      notes: draft.notes || undefined,
    };
    setSavingTier(tier);
    api
      .upsertTierMargin(tier, body)
      .then(() => {
        enqueueSnackbar(`${tier} tier margin saved.`, { variant: "success" });
        return load();
      })
      .catch((e) =>
        enqueueSnackbar(
          `Save failed: ${e?.response?.data?.detail || e?.message || e}`,
          { variant: "error" },
        ),
      )
      .finally(() => setSavingTier(null));
  };

  return (
    <Box sx={sectionCardSx}>
      <Flx ac g={1.5} sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #eef2f5" }}>
        <LayersOutlined sx={{ fontSize: 18, color: "#1F5AF2" }} />
        <Txt sx={sectionTitleSx}>DSCR Tier Margins</Txt>
        <Txt sx={{ fontSize: 11.5, color: "#94a3b8" }}>
          Bond points (par = 100). The total margin a correspondent prices to =
          tier margin + their additional margin.
        </Txt>
      </Flx>

      <Box sx={{ p: 2.5 }}>
        {loading ? (
          <Flx center sx={{ py: 4 }}>
            <CircularProgress size={20} sx={{ color: "#1F5AF2" }} />
          </Flx>
        ) : (
          <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, overflow: "auto" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
              <Box
                component="thead"
                sx={{
                  "& th": {
                    bgcolor: "#f1f6f9",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#475569",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    py: 1,
                    px: 1.5,
                    borderBottom: "1px solid #e5e7eb",
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  },
                  "& th:first-of-type": { textAlign: "left" },
                  "& th:last-of-type": { textAlign: "center" },
                }}
              >
                <Box component="tr">
                  <Box component="th">Tier</Box>
                  <Box component="th" sx={{ textAlign: "left !important" }}>
                    Label
                  </Box>
                  {NUM_COLS.map((c) => (
                    <Box component="th" key={c.key}>
                      {c.label}
                    </Box>
                  ))}
                  <Box component="th">Actions</Box>
                </Box>
              </Box>
              <Box
                component="tbody"
                sx={{
                  "& td": {
                    py: 0.5,
                    px: 1.5,
                    borderBottom: "1px solid #f1f5f9",
                    verticalAlign: "middle",
                  },
                  "& tr:last-of-type td": { borderBottom: "none" },
                  "& tr:hover": { bgcolor: "#f8fafc" },
                }}
              >
                {tiers.map((tier) => {
                  const draft = rows[tier];
                  const dirty = isDirty(tier);
                  return (
                    <Box component="tr" key={tier}>
                      <Box
                        component="td"
                        sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}
                      >
                        {tier}
                      </Box>
                      <Box component="td">
                        <TextField
                          size="small"
                          value={draft.label}
                          onChange={(e) => setCell(tier, "label", e.target.value)}
                          sx={{ width: 130, "& input": { py: 0.5, fontSize: 13 } }}
                        />
                      </Box>
                      {NUM_COLS.map((c) => (
                        <Box component="td" key={c.key} sx={{ textAlign: "right" }}>
                          <TextField
                            type="number"
                            size="small"
                            value={draft[c.key]}
                            onChange={(e) => setCell(tier, c.key, e.target.value)}
                            inputProps={{
                              step: c.step,
                              style: { textAlign: "right" },
                            }}
                            sx={{
                              width: 104,
                              "& input": {
                                py: 0.5,
                                fontSize: 13,
                                fontVariantNumeric: "tabular-nums",
                              },
                            }}
                          />
                        </Box>
                      ))}
                      <Box component="td" sx={{ textAlign: "center", whiteSpace: "nowrap" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setHistoryTier(tier)}
                          sx={{ minWidth: 0, px: 1, mr: 0.5, textTransform: "none" }}
                        >
                          <HistoryRounded sx={{ fontSize: 16 }} />
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          disabled={!dirty || savingTier === tier}
                          onClick={() => saveTier(tier)}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          {savingTier === tier ? "Saving…" : "Save"}
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <PricingConfigVersionDialog
        open={!!historyTier}
        onClose={() => setHistoryTier(null)}
        title={historyTier ? `${historyTier} tier — change history` : ""}
        load={historyTier ? () => api.tierMarginHistory(historyTier) : null}
      />
    </Box>
  );
}
